import React from 'react';
import { observer } from 'mobx-react';
import { getSnapshot } from 'mobx-state-tree';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { Card } from 'core/components/card';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import useErrors from 'common/hooks/use-errors';
import { InternalUwNotes } from 'common/components/internal-uw-notes';
import useAdditionalOffers from 'offer/hooks/use-additional-offers';
import { RecalculatingDim } from 'offer/components/recalculating-dim';
import { PolicyPickerCard } from 'offer/components/sidebar/components/policy-picker-card';
import { Additionals } from 'offer/components/sidebar/components/additionals';
import { AdditionalOffersSection } from 'offer/components/sidebar/components/additional-offers-section';
import { PolicyErrors } from 'offer/components/sidebar/components/policy-errors';
import { DocumentsSharing } from 'offer/components/sidebar/components/documents-sharing';
import { BundleBanner } from './bundle-banner';
import { PolicyCards } from './policy-cards';
import { Discounts } from './discounts';
import { Underwriting } from './underwriting';
import { QuoteTools } from './quote-tools';
import QuoteFacts from '../header/quote-facts';
import PriceSummary from './price-summary';
import useStyles from './left-column.styles';

/**
 * Modify Quote's left column for Staff Quote Revisit (#32313). Each redesigned section is its own
 * mount point in a sibling folder, so each lane only edits its own folder. Sections the redesign
 * doesn't touch render today's components unchanged.
 *
 * Condensed layout: this column sits right of the content, price first as in the prototype's quote page, then
 * the pre-bind ID, rep and quote source the header shows elsewhere, then the internal notes.
 */
const LeftColumn = observer(() => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const getErrorsAndRejections = (code) => code >= 4003;
  const errorsAndRejections = useErrors(getSnapshot(store.errors), getErrorsAndRejections);
  const additionalOffers = useAdditionalOffers(store);
  const condensed = isCondensedLayout();

  if ((store.loading && !store.recalculatingPrice) || !store.offer || additionalOffers.loading) {
    return (
      <>
        {!condensed && <div className={classes.columnHeader} />}
        <Loading className={classes.loading} />
      </>
    );
  }

  return (
    <RecalculatingDim id="offer_details" className={classes.leftColumn}>
      {!condensed && <div className={classes.columnHeader} />}
      {!condensed && <InternalUwNotes offerId={store.offer.webUserId || store.offer.id} />}
      {condensed ? (
        <PriceSummary />
      ) : (
        <>
          <PolicyPickerCard />
          <BundleBanner />
          <PolicyCards />
        </>
      )}
      {condensed && (
        <>
          <Card className={classes.factsCard}>
            <QuoteFacts condensed />
            <DocumentsSharing compact />
          </Card>
          <InternalUwNotes offerId={store.offer.webUserId || store.offer.id} />
        </>
      )}
      <Discounts />
      <Underwriting />
      <QuoteTools />
      {store.shouldShowAdditionalPaymentsSection && <Additionals />}
      {additionalOffers.shouldShowAdditionalOffers && (
        <AdditionalOffersSection {...additionalOffers.additionalOffersProps} />
      )}
      {errorsAndRejections.length > 0 && <PolicyErrors />}
      <DocumentsSharing />
    </RecalculatingDim>
  );
});

export default LeftColumn;
